import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, MessageSquare, Pencil, Plus, Share2 } from '@digital-twins/design-system/icons'
import {
  LibraryCollectionCover,
  LibraryCollectionDetail,
  LibraryRuleField,
  LibraryRuleLabel,
  canAddLibraryCollectionMembers,
} from '@digital-twins/shared-types'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { MarketLabel } from '@/components/ui/MarketLabel'
import { CollectionSharingBadge } from '@/components/library/CollectionSharingBadge'
import { LibraryCountPhrase } from '@/components/library/LibraryCountPhrase'
import {
  LIBRARY_HERO_LABEL_CLASS,
  LIBRARY_HERO_META_CLASS,
  LIBRARY_HERO_NUMBER_CLASS,
  LibraryHeroSurface,
} from '@/components/library/LibraryHeroSurface'
import { CollectionCoverMosaic } from '@/components/library/MosaicCollectionCard'
import { intlLocaleForUi } from '@/i18n'
import { marketDisplayName, marketFlagCode } from '@/pages/KnowledgeHubPage/libraryHelpers'
import { askLibraryCollectionPath } from '@/pages/ThreadsPage/threadHelpers'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'

export interface CollectionHeaderProps {
  collection: LibraryCollectionDetail
  /** The banner mosaic; empty draws one tile per resource kind the collection holds. */
  cover?: Array<LibraryCollectionCover>
  onAdd: () => void
  onEdit: () => void
  onDelete: () => void
  /** Opens the share dialog; the Share button shows only to the owner and only with this set. */
  onShare?: () => void
}

/** The featured banner's title scale (48 to 60 px), on the collection container and the deep surface. */
export const COLLECTION_TITLE_CLASS =
  'min-w-0 text-balance font-display text-[38px] font-normal leading-[1.04] tracking-[-0.015em] text-primary-dark-foreground @[760px]/collection:text-[48px] @[1160px]/collection:text-[60px]'

/** The Topic header's action buttons: icon only below 760, labelled above. */
const ACTION_CLASS = 'h-[34px] gap-2 rounded-lg px-3 font-medium [&>span]:hidden @[760px]/collection:[&>span]:inline'

const META_ITEM_CLASS =
  "inline-flex items-center gap-1.5 [&:not(:first-child)]:before:mx-2.5 [&:not(:first-child)]:before:h-[3px] [&:not(:first-child)]:before:w-[3px] [&:not(:first-child)]:before:rounded-full [&:not(:first-child)]:before:bg-primary-dark-foreground/40 [&:not(:first-child)]:before:content-['']"

/** The main action: the brand primary fill in every theme (the default deep fill melts into a dark page). */
const PRIMARY_ACTION_CLASS = 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md'

/** A date as "Oct 20, 2026" in the UI locale; null when unreadable. */
function formatMetaDate(iso: string, locale: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date)
  } catch {
    return null
  }
}

/**
 * The banner mosaic's covers (the detail carries the card's `cover`). A
 * response from before the field existed reads as none, and the banner then
 * draws kind tiles.
 */
export function collectionDetailCover(collection: LibraryCollectionDetail): Array<LibraryCollectionCover> {
  return Array.isArray(collection.cover) ? collection.cover : []
}

/** Placeholder tiles, in this order, for a banner without covers. */
const PLACEHOLDER_KINDS = ['document', 'study', 'audience', 'topic'] as const

/**
 * The banner's tiles: the given covers, else one icon tile per kind the
 * collection holds (documents and datasets share the document tile).
 */
export function bannerCover(
  collection: Pick<LibraryCollectionDetail, 'resourceCounts' | 'topicCount'>,
  cover: Array<LibraryCollectionCover> | undefined,
): Array<LibraryCollectionCover> {
  if (cover && cover.length > 0) {
    return cover
  }
  const counts: Record<(typeof PLACEHOLDER_KINDS)[number], number> = {
    document: collection.resourceCounts.document + collection.resourceCounts.dataset,
    study: collection.resourceCounts.study,
    audience: collection.resourceCounts.audience,
    topic: collection.topicCount,
  }
  return PLACEHOLDER_KINDS.filter((kind) => counts[kind] > 0).map((kind) => ({ kind, imageUrl: null, title: '' }))
}

export interface RuleChip {
  field: LibraryRuleField
  label: string
}

/** Rule chips, unique by label text; a market chip carries its flag. */
export function uniqueRuleChips(labels: Array<LibraryRuleLabel>): Array<RuleChip> {
  const seen = new Set<string>()
  const result: Array<RuleChip> = []
  for (const { field, label } of labels) {
    const text = label.trim()
    const key = text.toLocaleLowerCase()
    if (text && !seen.has(key)) {
      seen.add(key)
      result.push({ field, label: text })
    }
  }
  return result
}

/**
 * One chip per label text, in `ruleLabels` order: a brand matched by two
 * facet tokens and by a Topic brand reads as one rule.
 */
export function uniqueRuleLabels(labels: Array<LibraryRuleLabel>): Array<string> {
  return uniqueRuleChips(labels).map((chip) => chip.label)
}

/** The back link to the Library; also the only header of the not found and error states. */
export function CollectionBackLink() {
  const { t } = useTranslation('app')

  return (
    <Link
      href={ROUTES.knowledge}
      className="inline-flex items-center gap-1.5 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground"
      data-testid="collection-header-back"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {t('library.collections.header.back')}
    </Link>
  )
}

/**
 * The collection page banner, in the Topic header's language: the back link
 * and the actions ("Ask this collection" for every viewer; Share for the
 * owner; Add and an Edit menu with "Edit collection" and "Delete", which
 * confirms first, only for `canEdit`, since every picker write and edit
 * needs it), then the banner on the featured banner's deep surface
 * (`LibraryHeroSurface`): the preset pill, the display title, the description (or "Add a
 * description" for an editor), the meta row with the sharing badge and dot
 * separators, the rule chips (markets by name with their flags), and the
 * cover mosaic beside them.
 */
export function CollectionHeader(props: CollectionHeaderProps) {
  const { collection, cover, onAdd, onEdit, onDelete, onShare } = props

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const navigate = useNavigate()
  const { t, i18n } = useTranslation('app')
  const locale = intlLocaleForUi(i18n.language)
  const ruleChips = useMemo(() => uniqueRuleChips(collection.ruleLabels), [collection.ruleLabels])
  const updated = useMemo(() => formatMetaDate(collection.updatedAt, locale), [collection.updatedAt, locale])
  const tiles = useMemo(() => bannerCover(collection, cover), [collection, cover])
  const description = collection.description?.trim() ?? ''

  const onConfirmDelete = () => {
    setIsDeleteOpen(false)
    onDelete()
  }

  const onAsk = () => {
    navigate(askLibraryCollectionPath(collection.token))
  }

  const resourceTotal = Object.values(collection.resourceCounts).reduce((sum, count) => sum + count, 0)
  const hasTwins = collection.resourceCounts.audience > 0 && collection.twinsCount > 0

  const editMenu = collection.canEdit ? (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={ACTION_CLASS}
          aria-label={t('library.collections.header.edit')}
          data-testid="collection-header-edit-menu"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          <span>{t('library.collections.header.edit')}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px] rounded-xl">
        <DropdownMenuItem data-testid="collection-header-edit-collection" onSelect={onEdit}>
          {t('library.collections.header.editCollection')}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="collection-header-delete" onSelect={() => setIsDeleteOpen(true)}>
          {t('library.collections.header.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null

  const shareButton =
    collection.isOwner && onShare ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={ACTION_CLASS}
        aria-label={t('library.collections.header.share')}
        data-testid="collection-header-share"
        onClick={onShare}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
        <span>{t('library.collections.header.share')}</span>
      </Button>
    ) : null

  return (
    <header className="text-left" data-testid="collection-header">
      <div className="flex items-center justify-between gap-3 pt-5">
        <div className="min-w-0">
          <CollectionBackLink />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={ACTION_CLASS}
            aria-label={t('library.collections.header.ask')}
            data-testid="collection-header-ask"
            onClick={onAsk}
          >
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            <span>{t('library.collections.header.ask')}</span>
          </Button>
          {shareButton}
          {editMenu}
          {canAddLibraryCollectionMembers(collection) ? (
            <Button
              type="button"
              size="sm"
              className={`${ACTION_CLASS} ${PRIMARY_ACTION_CLASS}`}
              aria-label={t('library.collections.add')}
              data-testid="collection-header-add"
              onClick={onAdd}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span>{t('library.collections.add')}</span>
            </Button>
          ) : null}
        </div>
      </div>

      {/* The featured banner's deep surface, so "Open collection" lands on the same editorial frame. */}
      <LibraryHeroSurface testId="collection-banner" className="mb-6 mt-5">
        <div className="relative flex min-w-0 flex-col-reverse gap-6 p-5 @[760px]/collection:flex-row @[760px]/collection:items-center @[760px]/collection:gap-10 @[760px]/collection:p-10 @[1160px]/collection:p-11">
          <div className="min-w-0 flex-1">
            <p className={`mb-4 ${LIBRARY_HERO_LABEL_CLASS}`} data-testid="collection-meta-preset">
              {collection.origin === 'saved_filter'
                ? t('library.collections.header.savedFilter')
                : t(`library.collections.presets.${collection.preset}`)}
            </p>
            <h1 className={COLLECTION_TITLE_CLASS} data-testid="collection-title">
              {collection.name}
            </h1>
            {description ? (
              <p
                className="mt-4 line-clamp-3 max-w-[640px] text-[16px] leading-[1.55] text-primary-dark-foreground/80"
                data-testid="collection-description"
              >
                {description}
              </p>
            ) : collection.canEdit ? (
              <button
                type="button"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md text-[15px] text-primary-dark-foreground/65 underline decoration-dotted underline-offset-4 transition-colors hover:text-primary-dark-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="collection-description-add"
                onClick={onEdit}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                {t('library.collections.header.addDescription')}
              </button>
            ) : null}
            <ul
              className={`mt-6 flex flex-wrap items-center gap-y-1.5 ${LIBRARY_HERO_META_CLASS}`}
              data-testid="collection-meta-row"
            >
              <MetaItem testId="collection-meta-resources">
                <LibraryCountPhrase noun="resources" count={resourceTotal} numberClassName={LIBRARY_HERO_NUMBER_CLASS} />
              </MetaItem>
              <MetaItem testId="collection-meta-topics">
                <LibraryCountPhrase
                  noun="topics"
                  count={collection.topicCount}
                  numberClassName={LIBRARY_HERO_NUMBER_CLASS}
                />
              </MetaItem>
              {hasTwins ? (
                <MetaItem testId="collection-meta-twins">
                  <LibraryCountPhrase
                    noun="twins"
                    count={collection.twinsCount}
                    numberClassName={LIBRARY_HERO_NUMBER_CLASS}
                  />
                </MetaItem>
              ) : null}
              <MetaItem testId="collection-meta-sharing">
                <CollectionSharingBadge
                  sharingMode={collection.sharingMode}
                  showLabel
                  className="text-sm text-primary-dark-foreground/70"
                  testId="collection-sharing-badge"
                />
              </MetaItem>
            </ul>
            {/* Who and when on a deliberate second line: a wrapped row would start with a stray dot. */}
            <ul
              className={`mt-2 flex flex-wrap items-center gap-y-1.5 ${LIBRARY_HERO_META_CLASS}`}
              data-testid="collection-meta-byline"
            >
              {collection.createdBy ? (
                <MetaItem testId="collection-meta-creator">
                  <UserAvatar initials={collection.createdBy.initials} name={collection.createdBy.name} size="xs" />
                  {t('library.collections.header.createdBy', {
                    name: collection.createdBy.name,
                  })}
                </MetaItem>
              ) : null}
              {updated ? (
                <MetaItem testId="collection-meta-updated">
                  {t('library.collections.header.updated', { date: updated })}
                </MetaItem>
              ) : null}
            </ul>
            {ruleChips.length > 0 ? (
              <ul className="mt-4 flex flex-wrap gap-1.5" data-testid="collection-rule-chips">
                {ruleChips.map((chip, index) => (
                  <li
                    key={chip.label}
                    className="inline-flex max-w-full items-center rounded-full border border-primary-dark-foreground/15 bg-primary-dark-foreground/10 px-2.5 py-1 text-xs text-primary-dark-foreground/90"
                    data-testid={`collection-rule-chip-${index}`}
                    data-rule-field={chip.field}
                  >
                    {chip.field === 'markets' ? (
                      <MarketLabel
                        label={marketDisplayName(chip.label, i18n.language)}
                        code={marketFlagCode(chip.label)}
                        data-testid={`collection-rule-chip-${index}-market`}
                      />
                    ) : (
                      chip.label
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <CollectionCoverMosaic
            cover={tiles}
            testId="collection-banner-cover"
            className="aspect-[16/10] w-full max-w-[520px] shrink-0 rounded-[18px] bg-primary-dark-foreground/5 shadow-[0_24px_48px_-28px_hsl(var(--foreground)/0.6)] @[760px]/collection:w-[44%] @[760px]/collection:max-w-[480px]"
          />
        </div>
      </LibraryHeroSurface>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="collection-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('library.collections.header.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('library.collections.header.deleteBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="collection-delete-cancel">
              {t('library.collections.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              data-testid="collection-delete-confirm"
              onClick={onConfirmDelete}
            >
              {t('library.collections.header.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  )
}

interface MetaItemProps {
  testId: string
  children: ReactNode
}

/** A meta row item with the Topic header's dot separator. */
function MetaItem(props: MetaItemProps) {
  const { testId, children } = props

  return (
    <li className={META_ITEM_CLASS} data-testid={testId}>
      {children}
    </li>
  )
}
